// Angular
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  Inject,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';

// Angular Material
import { MatIconRegistry } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

// RxJS
import { Observable, Subject, takeUntil, tap } from 'rxjs';

// Third-party
import { environment } from '@environment';
import { OAuthService } from 'angular-oauth2-oidc';

// Component Library
import {
  FsxSnackbarComponent,
  FsxSnackbarTypeEnum,
} from 'fsx-component-library';

// Local
import { EmulationBannerComponent } from '@app/components/emulation-banner/emulation-banner.component';
import { FooterComponent } from '@app/components/footer/footer.component';
import { NavbarComponent } from '@app/components/navbar/navbar.component';
import { PagetitleComponent } from '@app/components/pagetitle/pagetitle.component';
import { PageHeadingFocusService } from '@app/services/accessibility/page-heading-focus.service';
import { CommonOptionsService } from '@app/services/common/common-options.service';
import {
  IdentityClaims,
  IdentityClaimsService,
} from '@app/services/identity-claims/identity-claims.service';
import { ErrorsStateService } from '@app/services/ui-state/errors-state/errors-state.service';
import { GenericStateService } from '@app/services/ui-state/generic-state/generic-state.service';
import { SuccessStateService } from '@app/services/ui-state/success-state/success-state.service';
import { WarningStateService } from '@app/services/ui-state/warning-state/warning-state.service';
import { LoadingStateService } from '@app/services/utilities/loading-state.service';
import { TrialBindersStateService } from '@app/trialbinder/services/ui-state/trial-binders-state.service';
import { FsxContact } from '@app/types/user/User';
import { AppConfig } from '@core/app-config/app-config.service';
import { AuthService } from '@core/auth/auth.service';
import { EmulationService } from '@core/auth/emulation.service';

/**
 * A function to load the configuration file.
 * @param appConfigService - The app config service to load the configuration file.
 * @returns An observable of the configuration file.
 */
export function loadConfig(
  appConfigService: AppConfig,
): () => Observable<string | Error> {
  return () => {
    return appConfigService.load$(environment.CONFIG_FILE);
  };
}

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterOutlet,
    ScrollingModule,
    MatProgressBarModule,
    NavbarComponent,
    FooterComponent,
    MatSidenavModule,
    MatSnackBarModule,
    PagetitleComponent,
    MatProgressSpinnerModule,
    EmulationBannerComponent,
  ],
})
export class AppComponent implements OnDestroy {
  /**
   * An observable representing the current user.
   * Used to display the user information in the template.
   */
  user$: Observable<FsxContact | null> = this.authService.sessionUser;

  /**
   * A subject to unsubscribe from observables onDestroy of component.
   * Used to unsubscribe from observables onDestroy of component.
   */
  private destroy$: Subject<unknown> = new Subject();

  /**
   * An observable indicating whether the user is authenticated or not.
   * Used to display the loading indicator in the template.
   */
  private isAuthenticated$: Observable<boolean> =
    this.authService.isAuthenticated.pipe(
      tap((isAuthenticated: boolean) => {
        if (isAuthenticated) {
          this.authService.stopSessionChecks();
        }
      }),
    );

  /**
   * Constructor for the AppComponent.
   *
   * @param matIconRegistry - The mat icon registry to register the icons.
   * @param domSanitizer - The dom sanitizer to sanitize the icons.
   * @param snackBar - The snack bar to show the messages.
   * @param authService - The auth service to get the user.
   * @param trialBinderStateService - The trial binder state service to get the error messages.
   * @param errorsStateService - The errors state service to get the error messages.
   * @param successStateService - The success state service to get the success messages.
   * @param warningStateService - The warning state service to get the warning messages.
   * @param genericStateService - The generic state service to get the generic messages.
   * @param oAuthService - The oauth service to get the identity claims.
   * @param identityClaimsService - The identity claims service to set the identity claims.
   * @param commonOptionsService - The common options service to load the options.
   * @param router - The router to navigate the application.
   * @param loadingStateService - The loading state service to manage the loading state.
   * @param emulationService
   * @param document
   */
  public constructor(
    private readonly matIconRegistry: MatIconRegistry,
    private readonly domSanitizer: DomSanitizer,
    private readonly snackBar: MatSnackBar,
    private readonly authService: AuthService,
    private readonly trialBinderStateService: TrialBindersStateService,
    private readonly errorsStateService: ErrorsStateService,
    private readonly successStateService: SuccessStateService,
    private readonly warningStateService: WarningStateService,
    private readonly genericStateService: GenericStateService,
    private readonly oAuthService: OAuthService,
    private readonly identityClaimsService: IdentityClaimsService,
    private readonly commonOptionsService: CommonOptionsService,
    private readonly router: Router,
    public readonly loadingStateService: LoadingStateService,
    private readonly emulationService: EmulationService,
    private readonly pageHeadingFocusService: PageHeadingFocusService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.pageHeadingFocusService.start();

    // Load the state options for the "State" FsxSelectComponent instances
    this.commonOptionsService.loadOptions();

    // Get the Identity Claims object using the third-party OAuthService.
    const identityClaims =
      this.oAuthService.getIdentityClaims() as IdentityClaims;

    // Store the Identity Claims into our own service.
    // - where the properties are exposes as observables.
    this.identityClaimsService.setIdentityClaims(identityClaims);

    // Re-apply emulation after a page refresh if a target is persisted. Must run after the real
    // identity claims are set so the overlay caches the correct admin claims.
    this.emulationService
      .restoreFromSession()
      .pipe(takeUntil(this.destroy$))
      .subscribe();

    // Subscribe to any error messages to show snack bar.
    this.trialBinderStateService.errorMessage$
      .pipe(
        takeUntil(this.destroy$),
        tap(error => {
          this.snackBar.openFromComponent(FsxSnackbarComponent, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: { message: error, snackType: FsxSnackbarTypeEnum.Error },
            panelClass: [FsxSnackbarTypeEnum.Error],
          });
        }),
      )
      .subscribe();

    // Subscribe to the router events to set the loading state.
    // - set the loading state to true when the user starts a navigation.
    // - set the loading state to false after navigation has completed successfully, cancelled, or errored.
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loadingStateService.setAppIsLoading(true);
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.loadingStateService.setAppIsLoading(false);
      }
    });

    // mat icon registry
    // All SVG icons inside the /assets/icons/ folder can be used by file name.
    // e.g., svgIcon="home" for /assets/icons/home.svg
    this.matIconRegistry.addSvgIconResolver(name =>
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        `../assets/icons/${name}.svg`,
      ),
    );

    // Subscribe to the authenticated state to stop session checks.
    // - stop session checks when the user is authenticated.
    this.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe();

    // Subscribe to the error messages to show error message snack bar.
    this.errorsStateService.errorMessage$
      .pipe(
        takeUntil(this.destroy$),
        tap(error => {
          this.snackBar.openFromComponent(FsxSnackbarComponent, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: { message: error, snackType: FsxSnackbarTypeEnum.Error },
            panelClass: [FsxSnackbarTypeEnum.Error],
          });
        }),
      )
      .subscribe();

    // Subscribe to the long error messages to show long error message snack bar.
    // These carry more than one problem at a time, so they stay up long enough to read.
    this.errorsStateService.longErrorMessage$
      .pipe(
        takeUntil(this.destroy$),
        tap(error => {
          this.snackBar.openFromComponent(FsxSnackbarComponent, {
            duration: 15000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: { message: error, snackType: FsxSnackbarTypeEnum.Error },
            panelClass: [FsxSnackbarTypeEnum.Error],
          });
        }),
      )
      .subscribe();

    // Subscribe to the success messages to show success message snack bar.
    this.successStateService.successMessage$
      .pipe(
        takeUntil(this.destroy$),
        tap(message => {
          this.snackBar.openFromComponent(FsxSnackbarComponent, {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: { message: message, snackType: FsxSnackbarTypeEnum.Success },
            panelClass: [FsxSnackbarTypeEnum.Success],
          });
        }),
      )
      .subscribe();

    // Subscribe to the long success messages to show long success message snack bar.
    this.successStateService.longSuccessMessage$
      .pipe(
        takeUntil(this.destroy$),
        tap(message => {
          this.snackBar.openFromComponent(FsxSnackbarComponent, {
            duration: 15000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: { message: message, snackType: FsxSnackbarTypeEnum.Success },
            panelClass: [FsxSnackbarTypeEnum.Success],
          });
        }),
      )
      .subscribe();

    // Subscribe to the warning messages to show warning message snack bar.
    this.warningStateService.warningMessage$
      .pipe(
        takeUntil(this.destroy$),
        tap(message => {
          this.snackBar.openFromComponent(FsxSnackbarComponent, {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: { message: message, snackType: FsxSnackbarTypeEnum.Warning },
            panelClass: [FsxSnackbarTypeEnum.Warning],
          });
        }),
      )
      .subscribe();

    // Subscribe to the generic messages to show generic message snack bar.
    this.genericStateService.genericMessage$
      .pipe(
        takeUntil(this.destroy$),
        tap(message => {
          this.snackBar.openFromComponent(FsxSnackbarComponent, {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            data: { message: message, snackType: FsxSnackbarTypeEnum.Default },
            panelClass: [FsxSnackbarTypeEnum.Default],
          });
        }),
      )
      .subscribe();
  }

  /**
   * Moves focus straight to the main content region instead of following the skip
   * link's href. With `<base href="/">` in index.html, a plain `href="#main-content"`
   * resolves against the base URL rather than the current route, so the browser
   * treats it as a real navigation to "/" (a full page reload) instead of an
   * in-page fragment jump. Handling the click ourselves avoids that entirely.
   */
  public skipToMainContent(event: Event): void {
    event.preventDefault();
    this.document.getElementById('main-content')?.focus();
  }

  /**
   * Angular lifecycle method to unsubscribe from observables onDestroy of component.
   */
  public ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
